import React, { useState } from 'react';
import { budgetPlanningData } from '../../data/financialManagementData';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { AlertTriangle, CheckCircle, TrendingUp, Activity, DollarSign, Download, Filter, Calendar } from 'lucide-react';

const BudgetPlanningDashboard = () => {
  // Helper function to get badge variant based on status
  const getBadgeVariant = (status) => {
    switch(status) {
      case 'on-budget': return 'outline';
      case 'under-budget': return 'success';
      case 'slightly-over-budget': return 'warning';
      case 'over-budget': return 'destructive';
      default: return 'default';
    }
  };
  
  // Helper function to get status text
  const getStatusText = (status) => {
    switch(status) {
      case 'on-budget': return '預算內';
      case 'under-budget': return '低於預算';
      case 'slightly-over-budget': return '略超預算';
      case 'over-budget': return '超出預算';
      default: return '未知狀態';
    }
  };
  const { departmentalBudgets, expenseCategoryBudgets, budgetVarianceReports, budgetKPIs } = budgetPlanningData;

  // Format currency
  const formatCurrency = (amount, currency = 'HKD') => {
    return new Intl.NumberFormat('zh-HK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate percentage
  const calculatePercentage = (used, total) => {
    return Math.round((used / total) * 100);
  };

  // Prepare data for departmental budget pie chart
  const departmentalBudgetData = departmentalBudgets.map((dept) => ({
    name: dept.department_name,
    value: dept.total_budget,
  }));

  // Prepare data for expense category bar chart
  const expenseCategoryData = expenseCategoryBudgets.map((category) => ({
    name: category.category_name,
    used: category.budget_used,
    remaining: category.budget_remaining,
  }));

  // State for selected report
  const [selectedReportIndex, setSelectedReportIndex] = useState(0);
  
  // Prepare data for budget variance chart
  const varianceData = budgetVarianceReports[selectedReportIndex]?.departments.map((dept) => ({
    name: dept.department_name,
    planned: dept.planned_budget,
    actual: dept.actual_spend,
    variance: dept.variance_amount,
  }));

  // Colors for the pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h2 className="text-2xl font-bold mb-4">預算規劃儀表板</h2>
          <p className="text-gray-600 mb-4">
            查看部門和費用類別的預算分配、使用情況和差異分析，以優化資源分配和財務規劃。
          </p>
          <div className="relative h-60 rounded-lg overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2426&q=80"
              alt="Budget Planning"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/50 flex items-end p-6">
              <h3 className="text-white text-2xl font-bold">有效的預算規劃是實現財務目標的基礎</h3>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {budgetKPIs.map((kpi) => (
            <Card key={kpi.kpi_id} className={`shadow-md ${kpi.status === 'excellent' ? 'border-l-4 border-green-500' : kpi.status === 'good' ? 'border-l-4 border-blue-500' : 'border-l-4 border-yellow-500'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">{kpi.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-2xl font-bold">
                      {kpi.unit === '%' ? `${kpi.current_value}%` : kpi.current_value}
                    </div>
                    <p className="text-xs text-gray-500">目標: {kpi.unit === '%' ? `${kpi.target_value}%` : kpi.target_value}</p>
                  </div>
                  <div className={`p-2 rounded-full ${kpi.trend === 'improving' ? 'bg-green-100' : kpi.trend === 'stable' ? 'bg-blue-100' : 'bg-yellow-100'}`}>
                    {kpi.trend === 'improving' ? <TrendingUp className="h-5 w-5 text-green-600" /> : 
                     kpi.trend === 'stable' ? <Activity className="h-5 w-5 text-blue-600" /> : 
                     <TrendingUp className="h-5 w-5 text-yellow-600" />}
                  </div>
                </div>
                <Progress 
                  value={(kpi.current_value / kpi.target_value) * 100} 
                  className="h-1 mt-2" 
                  indicatorClassName={kpi.status === 'excellent' ? 'bg-green-500' : kpi.status === 'good' ? 'bg-blue-500' : 'bg-yellow-500'}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-xl">部門預算分配</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentalBudgetData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {departmentalBudgetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-xl">費用類別預算使用情況</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={expenseCategoryData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="used" stackId="a" fill="#8884d8" name="已使用" />
                  <Bar dataKey="remaining" stackId="a" fill="#82ca9d" name="剩餘" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 mb-8">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <CardTitle className="text-xl">{budgetVarianceReports[selectedReportIndex]?.report_name}</CardTitle>
                <div className="flex space-x-2">
                  {budgetVarianceReports.map((report, index) => (
                    <button
                      key={report.report_id}
                      onClick={() => setSelectedReportIndex(index)}
                      className={`px-3 py-1 text-xs rounded-full flex items-center ${selectedReportIndex === index 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      <Calendar className="h-3 w-3 mr-1" />
                      {report.fiscal_period}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={getBadgeVariant(budgetVarianceReports[selectedReportIndex]?.summary.overall_status)}>
                  {getStatusText(budgetVarianceReports[selectedReportIndex]?.summary.overall_status)}
                </Badge>
                <button className="p-2 rounded-full hover:bg-gray-100">
                  <Download className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-500 mb-1">
                <span>總計劃預算: {formatCurrency(budgetVarianceReports[selectedReportIndex]?.summary.total_planned)}</span>
                <span>總實際支出: {formatCurrency(budgetVarianceReports[selectedReportIndex]?.summary.total_actual)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>差異: {formatCurrency(budgetVarianceReports[selectedReportIndex]?.summary.total_variance_amount)}</span>
                <span className={budgetVarianceReports[selectedReportIndex]?.summary.total_variance_percentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {budgetVarianceReports[selectedReportIndex]?.summary.total_variance_percentage >= 0 ? '+' : ''}
                  {budgetVarianceReports[selectedReportIndex]?.summary.total_variance_percentage.toFixed(2)}%
                </span>
              </div>
            </div>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={varianceData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="planned" fill="#8884d8" name="計劃預算" />
                  <Bar dataKey="actual" fill="#82ca9d" name="實際支出" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 space-y-4">
              {budgetVarianceReports[selectedReportIndex]?.departments.map((dept) => (
                <div key={dept.department_id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-medium">{dept.department_name}</div>
                    <Badge 
                      variant={dept.status === 'on-budget' ? 'outline' : dept.status === 'under-budget' ? 'success' : 'destructive'}
                      className="ml-2"
                    >
                      {dept.status === 'on-budget' ? '預算內' : dept.status === 'under-budget' ? '低於預算' : '超出預算'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>計劃: {formatCurrency(dept.planned_budget)}</span>
                    <span>實際: {formatCurrency(dept.actual_spend)}</span>
                    <span className={dept.variance_amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                      差異: {formatCurrency(dept.variance_amount)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Progress 
                      value={(dept.actual_spend / dept.planned_budget) * 100} 
                      className="h-2 flex-grow" 
                      indicatorClassName={dept.status === 'under-budget' ? 'bg-green-500' : dept.status === 'on-budget' ? 'bg-blue-500' : 'bg-red-500'}
                    />
                    <span className="ml-2 text-xs">
                      {calculatePercentage(dept.actual_spend, dept.planned_budget)}%
                    </span>
                  </div>
                  {dept.notes && (
                    <div className="mt-2 text-xs text-gray-500">
                      {dept.status === 'over-budget' ? 
                        <div className="flex items-center text-red-600">
                          <AlertTriangle className="h-3 w-3 mr-1" /> {dept.notes}
                        </div> : 
                        dept.status === 'under-budget' ? 
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" /> {dept.notes}
                        </div> : 
                        dept.notes
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {departmentalBudgets.slice(0, 6).map((budget) => (
          <Card key={budget.budget_id} className="shadow-md">
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle>{budget.department_name}</CardTitle>
                <Badge variant={budget.status === 'active' ? 'default' : 'outline'}>
                  {budget.status === 'active' ? '活躍' : '非活躍'}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">財政年度: {budget.fiscal_year}</p>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="text-2xl font-bold">{formatCurrency(budget.total_budget, budget.currency)}</div>
                  <p className="text-sm text-gray-500">總預算</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>已使用</span>
                  <span>{formatCurrency(budget.budget_used, budget.currency)}</span>
                </div>
                <Progress 
                  value={(budget.budget_used / budget.total_budget) * 100} 
                  className="h-2" 
                  indicatorClassName="bg-purple-500"
                />
                <div className="flex justify-between text-sm">
                  <span>剩餘</span>
                  <span className="font-medium">{formatCurrency(budget.budget_remaining, budget.currency)}</span>
                </div>
              </div>
              
              <div className="mt-4 text-xs text-gray-500">
                最後更新: {budget.last_updated}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BudgetPlanningDashboard;
